import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { AUTH_MODE } from '../../lib/runtimeFlags';
import { useTranslation } from 'react-i18next';
import { useAuthStore, selectIsSuperAdmin } from '../../stores/authStore';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import authService from '../../services/authService';
import userService from '../../services/userService';
import { User, UserRole, UserRoles } from '../../types';
import { rememberReturnTo } from '../../lib/authReturnTo';

interface ProtectedRouteProps {
  // Aucune prop spécifique nécessaire pour l'instant, mais on peut en ajouter
  // par exemple, pour vérifier des rôles/permissions spécifiques plus tard.
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = () => {
  const { isAuthenticated: isLogtoAuthenticated, isLoading: isLogtoLoading, signIn, signOut, getIdToken } = useAuth();
  const isStoreAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const { actions } = useAuthStore();
  const location = useLocation();
  const { t } = useTranslation();
  const [hasTriedSignIn, setHasTriedSignIn] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showAccountDeactivated, setShowAccountDeactivated] = useState(
    () => !!sessionStorage.getItem('auth_account_deactivated_role')
  );
  const [deactivatedUserRole, setDeactivatedUserRole] = useState<string>(
    () => sessionStorage.getItem('auth_account_deactivated_role') ?? UserRoles.User
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const syncAttemptsRef = useRef(0);
  const MAX_SYNC_ATTEMPTS = 2; // initial attempt + 1 retry — bounded to prevent infinite loops
  const [syncRetryTrigger, setSyncRetryTrigger] = useState(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [countdown, setCountdown] = useState(30);

  // Apply user preferences (theme, language) when authenticated
  useUserPreferences();

  // Demo users have a non-Logto session — skip all Logto auth logic in effects.
  // The store was already populated by DemoApp before navigating here.
  const isDemoUser = user?.role === 'demo';

  // Detect session expiration and clean local store (skipped for demo users)
  useEffect(() => {
    if (isDemoUser) return;

    // If Logto says not authenticated but local store says yes, session has expired
    if (!isLogtoLoading && !isLogtoAuthenticated && isStoreAuthenticated) {
      console.log('Session expired detected - cleaning local store');
      actions.logout();
      setShowSessionExpired(true);
      setCountdown(30); // Reset countdown to 30 seconds
    }
    // Also check if user is on a protected route but not authenticated
    // This handles the case where the store was already cleared
    else if (!isLogtoLoading && !isLogtoAuthenticated && !isStoreAuthenticated && !hasTriedSignIn) {
      // Check if we're on a protected route (not root or callback)
      const isProtectedRoute = location.pathname !== '/' &&
                               location.pathname !== '/callback' &&
                               !location.pathname.startsWith('/no-organization') &&
                               !location.pathname.startsWith('/organization-deactivated');

      // If on a protected route, assume session expired
      if (isProtectedRoute) {
        console.log('Session expired detected - on protected route without auth');
        setShowSessionExpired(true);
        setCountdown(30); // Reset countdown to 30 seconds
      }
    }
  }, [isDemoUser, isLogtoLoading, isLogtoAuthenticated, isStoreAuthenticated, hasTriedSignIn, location.pathname, actions]);

  // Countdown timer for session expiration
  useEffect(() => {
    if (!showSessionExpired) return;

    if (countdown <= 0) {
      // Redirect to Logto sign-in when countdown reaches 0
      rememberReturnTo(location.pathname + location.search + location.hash);
      signIn(window.location.origin + '/callback');
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [showSessionExpired, countdown, signIn, location.pathname, location.search, location.hash]);

  // Clear any pending retry timeout on unmount to avoid stale callbacks
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  // Trigger Logto sign-in ONLY if not authenticated and not loading (skipped for demo users)
  // This is for the first entry to the app
  useEffect(() => {
    if (isDemoUser) return;
    if (!isLogtoLoading && !isLogtoAuthenticated && !hasTriedSignIn && !showSessionExpired && !showAccountDeactivated) {
      setHasTriedSignIn(true);
      // Redirect to Logto sign-in, then back to /callback
      // This is safe as there is no session yet
      rememberReturnTo(location.pathname + location.search + location.hash);
      signIn(window.location.origin + '/callback');
    }
  }, [isDemoUser, isLogtoLoading, isLogtoAuthenticated, signIn, hasTriedSignIn, showSessionExpired, showAccountDeactivated, location.pathname, location.search, location.hash]);

  /**
   * SILENT SYNC: If Logto is authenticated but our store is not (limbo state),
   * try to sync in-place instead of redirecting to Logto.
   * This prevents "invalid_credentials" errors caused by multiple OIDC flows.
   */
  const isSyncingRef = useRef(isSyncing);
  useEffect(() => { isSyncingRef.current = isSyncing; });

  const performSilentSync = useCallback(async () => {
    if (isSyncingRef.current) return;
    if (syncAttemptsRef.current >= MAX_SYNC_ATTEMPTS) return;

    setIsSyncing(true);
    syncAttemptsRef.current += 1;
    const attemptNumber = syncAttemptsRef.current;

    console.log(`ProtectedRoute: Attempting silent sync (${attemptNumber}/${MAX_SYNC_ATTEMPTS})...`);

    try {
      const token = await getIdToken();
      if (!token) throw new Error('No ID token available');

      const backendUser = await authService.syncUserWithBackend(token);
      console.log('[ProtectedRoute] backendUser from /auth/me:', backendUser);
      console.log('[ProtectedRoute] backendUser.profile:', (backendUser as any).profile);

      const syncedUser: User = {
        id: backendUser.id,
        email: backendUser.email,
        name: backendUser.name || `${backendUser.first_name || ''} ${backendUser.last_name || ''}`.trim() || backendUser.email,
        role: (backendUser.role as UserRole) || UserRoles.User,
        is_active: backendUser.is_active,
        organization_id: backendUser.organization_id,
        organization_is_active: (backendUser as any).organization_is_active,
        organization_is_demo: (backendUser as any).organization_is_demo ?? null,
        avatar_url: backendUser.avatar_url,
        preferences: backendUser.preferences,
        logto_id: (backendUser as any).logto_id,
        profile_id: (backendUser as any).profile_id ?? null,
        profile: (backendUser as any).profile ?? null,
      };

      actions.loginSuccess({ access_token: token }, syncedUser);
      sessionStorage.removeItem('auth_sync_failed');
      sessionStorage.removeItem('auth_account_deactivated_role');
      syncAttemptsRef.current = 0;
      console.log('ProtectedRoute: Silent sync successful');
    } catch (error: any) {
      console.error('ProtectedRoute: Silent sync failed:', error);

      // Detect inactive user specifically (HTTP 400 with "Inactive user" detail)
      const detail = error?.response?.data?.detail;
      const isInactiveUser =
        (typeof detail === 'object' && detail?.message === 'Inactive user') ||
        detail === 'Inactive user';

      if (isInactiveUser) {
        const role = typeof detail === 'object' ? (detail?.role ?? UserRoles.User) : UserRoles.User;
        sessionStorage.setItem('auth_account_deactivated_role', role);
        setDeactivatedUserRole(role);
        setShowAccountDeactivated(true);
        return;
      }

      // For other errors: retry once, then redirect to login.
      // Counter is bounded by MAX_SYNC_ATTEMPTS so we cannot loop.
      if (attemptNumber >= MAX_SYNC_ATTEMPTS) {
        console.error('ProtectedRoute: Sync failed after retry, redirecting to login');
        sessionStorage.removeItem('auth_sync_failed');
        signOut(window.location.origin);
      } else {
        sessionStorage.setItem('auth_sync_failed', 'true');
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          setSyncRetryTrigger((n) => n + 1);
        }, 1500);
      }
    } finally {
      setIsSyncing(false);
    }
  }, [getIdToken, actions, signOut]);

  useEffect(() => {
    if (isDemoUser) return;
    const isDeactivated = !!sessionStorage.getItem('auth_account_deactivated_role');
    if (
      !isLogtoLoading &&
      isLogtoAuthenticated &&
      !isStoreAuthenticated &&
      !showSessionExpired &&
      !showAccountDeactivated &&
      !isDeactivated &&
      syncAttemptsRef.current < MAX_SYNC_ATTEMPTS
    ) {
      performSilentSync();
    }
  }, [isDemoUser, isLogtoLoading, isLogtoAuthenticated, isStoreAuthenticated, showSessionExpired, showAccountDeactivated, performSilentSync, syncRetryTrigger]);

  // Refresh user profile once on load when already authenticated (e.g. after role change like promote to org admin)
  const userRefreshDoneRef = useRef(false);
  useEffect(() => {
    // Demo users are refreshed by DemoApp itself; skip here to avoid overwriting organization_is_demo.
    if (isDemoUser || !isStoreAuthenticated || !user || userRefreshDoneRef.current) return;
    userRefreshDoneRef.current = true;
    console.log('[ProtectedRoute] refreshing user via /users/me (store authenticated)');
    userService.fetchCurrentUser()
      .then((backendUser) => {
        console.log('[ProtectedRoute] /users/me backendUser:', backendUser);
        console.log('[ProtectedRoute] /users/me profile:', (backendUser as any).profile);
        const updatedUser: User = {
          id: backendUser.id,
          email: backendUser.email,
          name: backendUser.name ?? backendUser.email,
          role: (backendUser.role as UserRole) ?? UserRoles.User,
          is_active: backendUser.is_active,
          organization_id: backendUser.organization_id,
          organization_is_active: (backendUser as any).organization_is_active,
          organization_is_demo: (backendUser as any).organization_is_demo ?? null,
          avatar_url: backendUser.avatar_url,
          preferences: backendUser.preferences,
          logto_id: (backendUser as any).logto_id,
          profile_id: (backendUser as any).profile_id ?? null,
          profile: (backendUser as any).profile ?? null,
        };
        actions.setUser(updatedUser);
      })
      .catch((err) => {
        console.warn('ProtectedRoute: could not refresh user profile', err);
        userRefreshDoneRef.current = false;
      });
  }, [isStoreAuthenticated, user, actions]);

  // Demo users have a non-Logto session: bypass all Logto auth checks.
  // The store was already populated by DemoApp; just run the org/deactivation guards.
  if (isDemoUser && isStoreAuthenticated) {
    if (!user.organization_id) {
      if (location.pathname !== '/no-organization') {
        return <Navigate to="/no-organization" replace />;
      }
      return <Outlet />;
    }

    if (user.organization_is_active === false && !selectIsSuperAdmin(user)) {
      if (location.pathname !== '/organization-deactivated') {
        return <Navigate to="/organization-deactivated" replace />;
      }
      return <Outlet />;
    }

    return <Outlet />;
  }

  // Show account deactivated message
  if (showAccountDeactivated) {
    const isAdmin = deactivatedUserRole === UserRoles.Admin;
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center p-8 max-w-md">
          <div className="text-red-500 text-6xl mb-6">🚫</div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('auth.accountDeactivated.title')}
          </h1>
          <p className="text-gray-300 mb-8">
            {isAdmin
              ? t('auth.accountDeactivated.messageContactUs')
              : t('auth.accountDeactivated.messageContactAdmin')}
          </p>
          <button
            onClick={() => {
              sessionStorage.removeItem('auth_account_deactivated_role');
              signOut(window.location.origin);
            }}
            className="px-6 py-3 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors font-semibold"
          >
            {t('auth.accountDeactivated.signOut')}
          </button>
        </div>
      </div>
    );
  }

  // Show session expired message with countdown and auto-redirect
  if (showSessionExpired) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center p-8 max-w-md">
          <div className="text-yellow-500 text-6xl mb-6">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-4">{t('auth.sessionExpired.title')}</h1>
          <p className="text-gray-300 mb-8">
            {t('auth.sessionExpired.message')}
          </p>
          <div className="mb-8">
            <div className="text-6xl font-bold text-orange-500 mb-2">{countdown}</div>
            <p className="text-gray-400 text-sm">{t('auth.sessionExpired.secondsRemaining')}</p>
          </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowSessionExpired(false);
                  setHasTriedSignIn(false);
                  rememberReturnTo(location.pathname + location.search + location.hash);
                  signIn(window.location.origin + '/callback');
                }}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
              >
                {t('auth.sessionExpired.signInNow')}
              </button>
            </div>
        </div>
      </div>
    );
  }

  // Demo users bypass all Logto UI — they authenticate via HS256 token in the store.
  if (isDemoUser && isStoreAuthenticated) {
    if (!user?.organization_id && location.pathname !== '/no-organization') {
      return <Navigate to="/no-organization" replace />;
    }
    return <Outlet />;
  }

  // Show loading state while Logto is initializing or redirecting to sign-in
  if (isLogtoLoading || !isLogtoAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">{t('auth.redirectingToLogin')}</p>
        </div>
      </div>
    );
  }

  // If Logto is authenticated but store is not yet synced, show a loading spinner.
  // The auto-retry flow in performSilentSync handles failures: one retry, then signOut → login.
  if (isLogtoAuthenticated && !isStoreAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-8 max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-bold mb-2">{t('auth.finalizingConnection')}</h2>
          <p className="text-gray-500">{t('auth.retrievingUserData')}</p>
        </div>
      </div>
    );
  }

  // Redirect users without an organization to the dedicated page.
  // Databricks mode: bootstrap super admins start without an organization and
  // must reach the Organizations page to create the first one.
  const isOrgLessSuperAdmin = AUTH_MODE === 'databricks' && !!user && selectIsSuperAdmin(user);
  if (isStoreAuthenticated && (!user || !user.organization_id) && !isOrgLessSuperAdmin) {
    if (location.pathname !== '/no-organization') {
      return <Navigate to="/no-organization" replace />;
    }
    return <Outlet />;
  }

  // Block users of inactive organizations (super admins bypass this)
  if (isStoreAuthenticated && user?.organization_is_active === false && !selectIsSuperAdmin(user)) {
    if (location.pathname !== '/organization-deactivated') {
      return <Navigate to="/organization-deactivated" replace />;
    }
    return <Outlet />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
